/**
 * The audit log.
 *
 * The screen someone reads under pressure — after a client asks who viewed their
 * footage, or after something was deleted that should not have been. Every
 * judgement about wording lives in auditViewModel and is tested there.
 *
 * Newest first, and destructive events marked, because those are what someone is
 * usually scrolling to find.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, fetchAudit, type AuditEvent } from './api.ts';
import { auditViewModel, type AuditCategory } from './auditViewModel.ts';

const FILTERS: Array<{ label: string; category: AuditCategory | 'all' }> = [
  { label: 'Everything', category: 'all' },
  { label: 'Who signed in', category: 'auth' },
  { label: 'Footage viewed', category: 'footage' },
  { label: 'Credentials shown', category: 'credential' },
  { label: 'Access changed', category: 'access' },
  { label: 'Destructive', category: 'destructive' },
];

export function Audit({ now }: { now: number }): React.JSX.Element {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AuditCategory | 'all'>('all');

  const load = useCallback(async () => {
    try {
      setEvents(await fetchAudit());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('Only owners can read the audit log.');
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Could not load the audit log.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = auditViewModel(events ?? [], now).filter(
    (row) => filter === 'all' || row.category === filter,
  );

  if (error && events === null) {
    return (
      <section className="section">
        <div className="empty">
          <p className="empty__title">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section__head">
        <p className="section__meta">
          {/* Said plainly: this is a record, not a feed that can be tidied. */}
          Append-only. Nothing here can be edited or removed through this application.
        </p>
        <button type="button" className="btn btn--sm" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      <div className="audit__filters">
        {FILTERS.map((entry) => (
          <button
            key={entry.category}
            type="button"
            className={filter === entry.category ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
            onClick={() => setFilter(entry.category)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {events === null ? (
        <p className="empty">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty">
          <p className="empty__title">Nothing recorded yet</p>
          <p>
            Sign-ins, access changes, credentials shown and anything destructive appear here as
            they happen.
          </p>
        </div>
      ) : (
        <ul className="audit">
          {rows.map((row) => (
            <li key={row.id} className={row.destructive ? 'audit__row audit__row--destructive' : 'audit__row'}>
              <span className="audit__summary">{row.summary}</span>
              <span className="audit__meta">
                {row.whenLabel}
                {row.sourceIp ? ` · from ${row.sourceIp}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
